// Every validation rule the enquiry forms enforce, in one place. Pure
// functions only — no React, no DOM — so the same rules can run on blur, on
// keystroke and on submit without the three ever quietly drifting apart.
//
// Required fields are exactly what they were before this file existed: Name
// and Phone/Email. Everything else here is format and length checking, never
// a new requirement — this is the business's live lead form, and turning an
// optional field into a mandatory one costs enquiries, not data quality.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value) {
  return EMAIL_RE.test(String(value ?? "").trim());
}

// Tolerant of how people actually type a number into a text box: spaces,
// hyphens, parenthesised STD/country codes and dots are all just visual
// grouping, not part of the number. One leading `+` survives the strip since
// it's meaningful (international prefix); everything else has to be a digit.
// 7–15 digits afterwards covers everything from a local landline to a full
// E.164 number (15 digits is E.164's own ceiling) without trying to validate
// a specific country's numbering plan.
export function isPhone(value) {
  const cleaned = String(value ?? "").trim().replace(/[\s\-().]/g, "");
  return /^\+?\d{7,15}$/.test(cleaned);
}

function requiredName(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "Enter your name.";
  if (trimmed.length < 2) return "Enter your full name.";
  if (trimmed.length > 80) return "That name is too long.";
  return null;
}

function requiredContact(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "Enter a phone number or email address so we can reply.";
  if (!isEmail(trimmed) && !isPhone(trimmed)) {
    return "That doesn't look like a phone number or an email address.";
  }
  return null;
}

// Every optional field is the same shape: fine empty, too long past some
// ceiling. Centralising it means the ceiling and its message are the only
// thing that varies per field.
function optionalMaxLength(value, max, message) {
  const trimmed = String(value ?? "").trim();
  if (trimmed.length > max) return message;
  return null;
}

// Submit order on the contact page — used so a failed submit can focus the
// *first* invalid control rather than whichever one happens to fail last.
export const CONTACT_FIELD_ORDER = ["name", "company", "city", "contact", "product", "requirement"];

// Submit order in the product enquiry popup.
export const PRODUCT_FIELD_ORDER = [
  "name",
  "contact",
  "company",
  "city",
  "quantity",
  "grammage",
  "size",
  "description",
];

export function validateContactForm(form) {
  const errors = {};
  const nameError = requiredName(form.name);
  if (nameError) errors.name = nameError;

  const contactError = requiredContact(form.contact);
  if (contactError) errors.contact = contactError;

  const companyError = optionalMaxLength(form.company, 100, "That company name is too long.");
  if (companyError) errors.company = companyError;

  const cityError = optionalMaxLength(form.city, 60, "That city name is too long.");
  if (cityError) errors.city = cityError;

  // `product` has no rule — it's a select drawn from a fixed list, so there's
  // nothing a visitor could type wrong.

  const requirementError = optionalMaxLength(
    form.requirement,
    2000,
    "That's longer than we can accept — please shorten it."
  );
  if (requirementError) errors.requirement = requirementError;

  return errors;
}

export function validateProductForm(form) {
  const errors = {};
  const nameError = requiredName(form.name);
  if (nameError) errors.name = nameError;

  const contactError = requiredContact(form.contact);
  if (contactError) errors.contact = contactError;

  const companyError = optionalMaxLength(form.company, 100, "That company name is too long.");
  if (companyError) errors.company = companyError;

  const cityError = optionalMaxLength(form.city, 60, "That city name is too long.");
  if (cityError) errors.city = cityError;

  const quantityError = optionalMaxLength(form.quantity, 80, "That quantity is too long.");
  if (quantityError) errors.quantity = quantityError;

  const grammageError = optionalMaxLength(form.grammage, 80, "That grammage is too long.");
  if (grammageError) errors.grammage = grammageError;

  const sizeError = optionalMaxLength(form.size, 80, "That size is too long.");
  if (sizeError) errors.size = sizeError;

  const descriptionError = optionalMaxLength(
    form.description,
    2000,
    "That's longer than we can accept — please shorten it."
  );
  if (descriptionError) errors.description = descriptionError;

  return errors;
}
